// URL building utilities for consistent customer links
import { useTenantSlug } from "@/stores/tenantStore";

export const PUBLIC_PREVIEW_BASE_URL = 'https://preview--braisepos.lovable.app';
export const PUBLIC_PROD_BASE_URL = 'https://braisepos.com';

export interface BuildCustomerUrlParams {
  subdomain: string;
  qrSlug: string;
  isDemo: boolean;
  domainVerified?: boolean;
}

/**
 * Canonical URL building strategy for customer-facing links
 */
export function buildCustomerUrl({ subdomain, qrSlug, isDemo, domainVerified = false }: BuildCustomerUrlParams): string {
  if (isDemo) {
    return `${PUBLIC_PREVIEW_BASE_URL}/r/${subdomain}/table/${qrSlug}`;
  }

  if (!isDemo && domainVerified) {
    return `https://${subdomain}.braisepos.com/table/${qrSlug}`;
  }

  // For unverified domains, use preview URL
  return `${PUBLIC_PREVIEW_BASE_URL}/r/${subdomain}/table/${qrSlug}`;
}

export interface BuildPublicTableUrlParams {
  subdomain: string;
  qrCodeSlug: string;
  token?: string | null | undefined;
  demo?: boolean;
  forceLocal?: boolean;
}

export function currentOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return import.meta?.env?.VITE_PUBLIC_ORIGIN || '';
}

/**
 * Build public table deep-link used for copy actions
 */
export function buildPublicTableUrl({
  subdomain,
  qrCodeSlug,
  token,
  demo = false,
  forceLocal = false,
}: BuildPublicTableUrlParams): string {
  const params = new URLSearchParams();
  if (demo) {
    params.set('mode', 'demo');
  }
  if (token) {
    params.set('token', token);
  }
  const query = params.toString();
  const path = `/r/${subdomain}/table/${qrCodeSlug}${query ? `?${query}` : ''}`;
  const isDev = import.meta.env.DEV || /localhost|127\.0\.0\.1/.test(currentOrigin());
  const origin = (isDev || forceLocal) ? currentOrigin() : (import.meta.env.VITE_PUBLIC_BASE_URL || currentOrigin());
  return `${origin}${path}`;
}

/**
 * Build ToGo tracking URL
 */
export function buildToGoUrl(subdomain: string, orderId: string, token: string, isDemo: boolean = false): string {
  const baseUrl = isDemo ? PUBLIC_PREVIEW_BASE_URL : PUBLIC_PREVIEW_BASE_URL; // For now, always use preview
  return `${baseUrl}/r/${subdomain}/togo/${orderId}?token=${token}`;
}

/**
 * Get the status badge text for the generated link
 */
export function getUrlStatusBadge(isDemo: boolean, domainVerified: boolean = false): string {
  if (isDemo) return 'Using Preview Host (Demo)';
  if (!domainVerified) return 'Using Preview Host (Unverified)';
  return 'Using Live Domain';
}

/**
 * Build a tenant-scoped path for an internal page
 */
export function tenantPath(slug: string | undefined | null, page: string = ""): string {
  const normalizedPage = page.replace(/^\/+/, "").replace(/\/+$/, "");
  if (!slug) {
    return normalizedPage ? `/${normalizedPage}` : "/";
  }
  return normalizedPage ? `/r/${slug}/${normalizedPage}` : `/r/${slug}`;
}

/**
 * Resolve a tenant-scoped href for navigation elements
 */
export function useTenantHref(page: string) {
  const slug = useTenantSlug();
  return tenantPath(slug, page);
}
